import { toPng } from 'html-to-image';

import normalizeHtmlForCKEditor from '@shared/utils/normalizeHtmlForCKEditor';

// ============================================================================
// 문서 캡쳐 유틸리티
// ============================================================================
//
// [사용처 안내]
//
// 1. 상신용 캡쳐 (모바일에서 볼 이미지)
//    - 함수: captureHtmlContent, captureHtmlContentAsBase64
//    - 사용처: useDocumentSave.js → handleSubmit()
//    - 용도: 문서 상신 시 본문 전체를 이미지로 캡쳐하여 서버에 저장
//           모바일에서 상신된 문서를 볼 때 이 이미지를 표시
//
// 2. 미리보기용 캡쳐 (PC에서 인쇄 미리보기)
//    - 함수: captureHtmlAsDataUrl
//    - 사용처: DocumentWritePage.jsx → handlePreview()
//              useDocumentActions.js → handlePreview()
//    - 용도: PC에서 문서 작성 중 인쇄 미리보기 표시
//
// 3. 관리자 재캡쳐 (기존 문서 이미지 일괄 재생성)
//    - 함수: captureHtmlContentAsBase64
//    - 사용처: ReCaptureDocumentImagePage.jsx
//
// [주의사항 — 반드시 읽고 수정할 것]
// - 상신·미리보기·재캡쳐 모두 동일한 iframe 렌더 파이프라인(captureHtmlToPngLayout)을 사용합니다.
// - width·padding·pixelRatio 등 레이아웃 옵션은 DOCUMENT_CAPTURE_DEFAULTS 한 곳에서 관리합니다.
//   → 호출부에서 옵션을 직접 하드코딩하지 마세요. DOCUMENT_CAPTURE_DEFAULTS를 spread해서 쓰세요.
//   → 미리보기만 pixelRatio를 높이고 싶은 등 부분 오버라이드는
//     { ...DOCUMENT_CAPTURE_DEFAULTS, pixelRatio: 3 } 식으로 하세요.
//
// [변경 히스토리]
// - (이전) 상신(width:734/padding:15)과 미리보기(width:754/padding:20)가 서로 다른 기본값을 사용.
//   → 같은 문서인데 상신 캡쳐 이미지와 미리보기 이미지의 줄바꿈·표 크기가 달라지는 문제 발생.
//   → 한쪽 캡쳐를 수정하면 다른 쪽 결과가 어긋나는 원인이 되었음.
// - (현재) DOCUMENT_CAPTURE_DEFAULTS로 통일 (width:734, padding:15, pixelRatio:2).
//   모든 호출부(상신·미리보기·재캡쳐)가 같은 기본값을 사용하므로,
//   captureHtmlToPngLayout 내부를 수정하면 모든 경로에 동일하게 반영됩니다.
// - (현재 정책) "표만 줄여서 캡처" — PNG 가로는 항상 baseWidth(734) 고정.
//   forceResponsiveTables: false — normalizeHtmlForCKEditor 에서 표 인라인 width 를 통째로 지우지 않음.
//   넓은 표는 injectedCss(래퍼 overflow:hidden, table width/max-width 100%, table-layout:fixed,
//   셀·자손 overflow-wrap:anywhere 등) + shrinkOverflowingTables + inlineTableCaptureStylesForHtmlToImage
//   조합으로 컨테이너 안에 맞춤.
// - (변경) 셀(td/th)·table outer 에 CKEditor 기본 콘텐츠 CSS 값을 명시적으로 재현.
//   iframe 캡처에는 CKEditor 기본 스타일이 로드되지 않아 셀이 padding 0·default border 없이 렌더되어
//   HTML 인라인 스타일(예: border:1px solid black)만 남으면 굵게 보였음.
//   CKEditor 디폴트(padding:.4em, cell 1px solid hsl(0,0%,75%), table outer 1px double hsl(0,0%,70%))을
//   !important 없이 주입 → 인라인 스타일이 있으면 인라인이 우선.
// - (이전 시도 실패 기록) 표 자연 폭에 맞춰 PNG 를 확장하고 표시 측에서 비례 축소하는 방식은
//   본문·헤더 텍스트까지 함께 축소되어 "페이지 전체가 쪼그라든" 결과가 되어 롤백.
//
// [향후 수정 시 주의사항]
// 1. DOCUMENT_CAPTURE_DEFAULTS를 변경하면 상신·미리보기·재캡쳐 전부 영향을 받습니다.
//    변경 전 세 경로 모두 테스트하세요.
// 2. captureHtmlToPngLayout 안의 normalizeHtmlForCKEditor 옵션을 바꾸면
//    표 폭·줄바꿈·margin 등이 달라질 수 있습니다.
//    호출부에서 따로 normalizeHtmlForCKEditor를 돌리지 마세요(이중 정규화 방지).
// 3. 넓은 표는 PNG 가로를 키우지 않고 injectedCss 로 컨테이너(734 - 40px) 안에 눌러 맞춥니다.
//    PNG 가로는 항상 baseWidth 고정이므로 DocumentPreview 슬라이스 top/높이는 추가 보정 없이 그대로 사용.
// 4. pixelRatio를 올리면 선명해지지만 PNG 용량이 비례하여 커집니다.
//    상신용은 서버에 저장되므로 2를 권장합니다.
//
// ============================================================================
// [상신 캡처 captureHtmlContent — 왜 이렇게 구현했는지]
//
// 1) iframe + 완전한 HTML 문서
//    - 메인 문서에 div만 붙여 toPng 하면 SVG foreignObject 가 표(table)를 제대로 못 그리거나
//      잘리는 브라우저가 있어, iframe 안에서 최소 HTML 문서를 연 뒤 .ck-content 에 본문을 넣고 캡처함.
//
// 2) figure.table → div.table (unwrapFigureTablesForCapture)
//    - CKEditor5는 표를 figure.table 로 감쌈. 동일한 엔진에서도 foreignObject 경로는 figure 내부 표가
//      통째로 비는 경우가 있어, 캡처용 DOM에서만 div.table 로 바꾸고 클래스는 유지해 스타일은 맞춤.
//    - 반드시 rootEl.ownerDocument.createElement 사용: iframe 문서 노드와 부모 document 가 다르면
//      삽입/레이아웃이 깨질 수 있음.
//
// 3) 주입 CSS 로 래퍼(.table)·table 을 컨테이너 안쪽에 눌러 맞춘다.
//    - table-layout:fixed + overflow-wrap:anywhere 등으로 셀 min-content 가 래퍼를 밀지 않게 함.
//    - 래퍼 overflow:hidden 으로 최후에 clip( PNG 에 스크롤/삐져나감 박제 방지 ).
//    - 셀/테이블 border·padding 은 CKEditor 디폴트를 !important 없이 주입(인라인 스타일 우선).
//    - toPng 직전 inlineTableCaptureStylesForHtmlToImage 로 줄바꿈 규칙을 인라인 !important 로
//      한 번 더 박음(html-to-image 제약 — 아래 [표 잘림 재발 시] 참고).
//
// 4) PNG 높이는 documentElement.scrollHeight 를 쓰지 않고 root(.ck-content)만 측정
//    - 캡처용 iframe 세로가 과하게 크면 documentElement.scrollHeight 가 "본문"이 아니라 뷰포트(iframe)
//      높이에 가깝게 잡혀, PNG 아래가 수천 px 빈 여백으로 나오는 문제가 있었음.
//    - iframe 세로는 작게(내용 스크롤은 iframe 내부에서만 발생) 두고, 높이는 root 의
//      scrollHeight / offsetHeight / getBoundingClientRect 만 반영.
//
// 5) 본문이 길어질 때
//    - root 안에 들어간 HTML 전체가 한 덩어리로 레이아웃되므로 root.scrollHeight 가 그만큼 커짐.
//      별도 "최대 높이" 제한을 두지 않았으므로 문단이 많아지면 이미지 세로도 그에 맞게 길어지고,
//      상·하단 불필요한 빈 공간만 제거한 상태로 전 구간이 캡처됨.
//
// 6) DB/에디터 본문과의 역할 분담
//    - 상신 시 서버에 저장되는 HTML은 에디터 출력 그대로(useDocumentSave 등에서 별도 forceResponsiveTables 없음).
//    - 표 폭 제거·iframe 캡처 등 무거운 처리는 captureHtmlToPngLayout(상신·미리보기 공통) 안에서만 수행.
//
// 7) 미리보기(captureHtmlAsDataUrl)
//    - 예전: 메인 document 의 숨김 div 에만 toPng → 표가 통째로 안 나오는 케이스가 상신과 동일하게 발생.
//    - 현재: 상신과 같은 iframe 파이프라인(captureHtmlToPngLayout)만 사용.
//    - 미리보기는 captureHtmlToPngLayout 결과의 { dataUrl, width, height }를 꺼내 슬라이스(페이지 분할)에 사용.
//
// ============================================================================
// [표 잘림 재발 시 — 무엇을 중점적으로 볼 것인가]
// ============================================================================
//
// ■ 증상으로 원인 갈라내기
//   - HTML 상세(CKEditorViewer 등)는 정상인데, 상신/미리보기 PNG 만 우측이 잘리거나 표가 넓게 박힘
//     → 캡처 파이프라인(iframe 렌더 + html-to-image toPng) 쪽을 본다. 에디터/뷰어 CSS 만으로는 재현 안 됨.
//   - 상세에서도 가로 스크롤·넘침이 보임
//     → 본문 HTML·뷰어 스타일(CKEditorViewer.module.scss 등)과 normalizeHtmlForCKEditor 옵션을 본다.
//
// ■ 캡처만 깨질 때 우선순위 (위에서부터)
//   1) html-to-image 의 clone 경로
//      - node_modules/html-to-image/es/clone-node.js 의 cloneCSSStyle 이
//        `window.getComputedStyle(노드)` 에 메인 문서 window 를 쓴다.
//      - 캡처 DOM 은 숨김 iframe(자식 document) 안에 있으므로, iframe <head> 에 주입한 injectedCss 가
//        복제 시 최종 스타일에 안 실리고, 셀 안 <p> 의 인라인 word-break:keep-all 만 남는 경우가 있다.
//      - 대응: inlineTableCaptureStylesForHtmlToImage — toPng 직전 DOM 에 인라인 !important 로 보강.
//   2) 붙여넣기 HTML (Word/Excel 등)
//      - 셀·문단에 word-break:keep-all, white-space:nowrap, 고정 width, col/colgroup width 합이 크면
//        table-layout:auto 시 min-content 가 커져 넘친다. (뷰어는 max-width:100% 등으로 덜 튈 수 있음)
//      - 대응: injectedCss 의 fixed 테이블 + overflow-wrap:anywhere, shrinkOverflowingTables, 위 인라인 보강.
//   3) toPng 의 캔버스/뷰박스 폭
//      - DOCUMENT_CAPTURE_DEFAULTS.width(734) 와 toPng 옵션 width 가 실제 클론 레이아웃보다 좁으면
//        SVG foreignObject 스냅샷에서 잘려 나간다. 폭 통일 여부·호출부 하드코딩을 확인.
//   4) 표 밖 본문
//      - pre, nowrap, 매우 긴 한 줄 문자열, 고정 width 블록이 .ck-content 폭을 밀면 동일하게 잘릴 수 있음.
//      - 표가 아니어도 injectedCss / 인라인 보강 범위 밖이면 별도 처리 필요.
//
// ■ 코드 위치 (수정 시)
//   - captureHtmlToPngLayout: injectedCss, shrinkOverflowingTables, inlineTableCaptureStylesForHtmlToImage 호출 순서
//   - unwrapFigureTablesForCapture: figure.table → div.table (foreignObject 호환)
//   - normalizeHtmlForCKEditor: forceResponsiveTables, stripTableMarginLeft 등이 표 폭에 영향
//
// ■ 로컬에서 빠르게 확인하는 방법
//   - 캡처용 iframe 을 잠깐 보이게 하거나, 동일 HTML 을 임시로 .ck-content 에 넣고
//     표의 offsetWidth / 래퍼 clientWidth, 셀 내부 getComputedStyle(...).overflowWrap·wordBreak 를 비교.
//   - 문제 표만 튄다면: 해당 행/열의 인라인 스타일·col width·문단 keep-all 여부를 먼저 본다.
//
// ============================================================================

// ============================================================================
// [공통 캡쳐 옵션] — 상신·미리보기·재캡쳐 전부 이 값을 기준으로 사용
// ============================================================================
// width: variables.scss $doc-body-content-width(734px)와 동일 → 작성/상세/미리보기 줄바꿈 일치
// padding: 에디터 본문 패딩과 동일(15px) → 텍스트 영역 실제 폭 = width - padding*2 = 704px
// pixelRatio: 2 → 상신용 서버 저장 기준. 미리보기에서 더 선명하게 하려면 호출부에서 오버라이드 가능.
//
// [이 값을 바꾸면 영향받는 곳]
// - useDocumentSave.js (상신 캡쳐)
// - DocumentWritePage.jsx (문서작성 미리보기)
// - useDocumentActions.js (문서상세 미리보기)
// - ReCaptureDocumentImagePage.jsx (관리자 재캡쳐)
// - calculatePageCount.js (미리보기 페이지 수 계산 — 별도 상수이나 동기화 필요)
const DOCUMENT_CAPTURE_DEFAULTS = {
  width: 734,
  padding: 15,
  quality: 1,
  pixelRatio: 2,
  backgroundColor: '#ffffff',
};

/**
 * 상신 캡처 PNG 높이: 반드시 .ck-content(root) 요소만 기준으로 잡는다.
 *
 * 왜 documentElement/body 의 scrollHeight 는 안 쓰나?
 * - 숨김 iframe 의 세로를 크게(예: 12000px) 잡으면 documentElement.scrollHeight 가 실제 본문이 아니라
 *   그 큰 뷰포트에 맞춰져, PNG 하단에 하얀 빈 영역이 끼는 현상이 발생함.
 *
 * 본문이 길어지면?
 * - root 에 담긴 전체 HTML 높이만큼 root.scrollHeight / offsetHeight 가 커지므로,
 *   긴 문서도 위에서 아래까지 한 번에 캡처 가능하다. iframe 의 화면에 보이는 높이(320px 등)와 무관.
 */
function measureCaptureContentHeight(rootEl) {
  const rectH = Math.ceil(rootEl.getBoundingClientRect().height);
  const h = Math.max(1, rootEl.scrollHeight, rootEl.offsetHeight, rectH);
  return Math.ceil(h);
}

/**
 * CKEditor5 는 표를 figure.table 로 감싸는데,
 * html-to-image(toPng → SVG foreignObject)에서 figure 안의 table 이 통째로 안 그려지는 브라우저가 있음.
 * 캡처용 DOM에서만 div.table 로 바꿔 동일 클래스로 스타일 유지.
 */
function unwrapFigureTablesForCapture(rootEl) {
  const ownerDoc = rootEl.ownerDocument || document;
  const figures = rootEl.querySelectorAll('figure.table');
  figures.forEach(function (fig) {
    const table = fig.querySelector('table');
    if (!table || !fig.parentNode) {
      return;
    }
    const wrap = ownerDoc.createElement('div');
    wrap.className = 'table';
    const figStyle = fig.getAttribute('style');
    if (figStyle) {
      wrap.setAttribute('style', figStyle);
    }
    fig.parentNode.insertBefore(wrap, fig);
    wrap.appendChild(table);
    fig.remove();
  });
}

/**
 * [캡처 전용] "넘치는 표만" 컨테이너 안으로 축소.
 *
 * 상세보기(CKEditorViewer)는 .table 래퍼의 overflow-x:auto 로 "넘치면 가로 스크롤"
 * 처리가 가능하지만, 우리는 PNG 로 박제하므로 스크롤이 존재할 수 없다. 그래서 넘치는 표는
 * "실제 폭 자체를 줄이는" 방법만 유효하다.
 *
 * [감지 전제 — 레거시]
 * fixed 레이아웃·인라인 보강으로 대부분 래퍼 안에 맞춰지나, 예전 col/td 고정 px 로만
 * 넘치는 경우에 한해 width 제거 + table 에 width:100% 인라인(보조).
 *
 * [축소 방법]
 * 해당 표의 table/td/th/col/colgroup 인라인 width·min-width 를 모두 제거하고,
 * table 에는 인라인 width:100% + max-width:100% 를 직접 박아 컨테이너 폭으로 잠근다.
 * 이후 table-layout:fixed + 줄바꿈 규칙으로 셀에 맞게 분배. 좁은 표는 건드리지 않는 것이
 * 원칙이었으나, 현재는 inlineTableCaptureStylesForHtmlToImage 가 모든 표에 width 100% 등을
 * 박는다(캡처 전용·상세 뷰와 별도).
 */
function shrinkOverflowingTables(rootEl) {
  const tables = rootEl.querySelectorAll('table');
  tables.forEach(function (table) {
    const wrapper = table.parentElement;
    if (!wrapper) {
      return;
    }
    const wrapperWidth = wrapper.clientWidth;
    if (!wrapperWidth) {
      return;
    }
    const naturalWidth = Math.max(table.offsetWidth, table.scrollWidth);
    if (naturalWidth <= wrapperWidth + 1) {
      return;
    }
    table.querySelectorAll('colgroup, col').forEach(function (col) {
      col.removeAttribute('width');
      if (col.style) {
        col.style.removeProperty('width');
        col.style.removeProperty('min-width');
      }
    });
    table.querySelectorAll('td, th').forEach(function (cell) {
      cell.removeAttribute('width');
      if (cell.style) {
        cell.style.removeProperty('width');
        cell.style.removeProperty('min-width');
      }
    });
    table.removeAttribute('width');
    if (table.style) {
      /* 넘침 표에 한해: 외부 HTML 의 table 인라인 width/max-width 가 남아 있으면
         주입 CSS 와 충돌할 수 있으므로, width/max-width 를 래퍼 기준 100% 로 통일. */
      table.style.setProperty('width', '100%', 'important');
      table.style.setProperty('max-width', '100%', 'important');
    }
  });
}

/**
 * html-to-image(toPng) 가 foreignObject 안에 넣는 **클론**은, 원본 노드에 대해
 * getComputedStyle 로 읽은 값을 인라인으로 복사한다(clone-node.js → cloneCSSStyle).
 *
 * 문제: 그때 사용하는 `window` 가 **호출한 쪽(메인 문서)** 이라, 캡처 root 가 들어 있는
 * **숨김 iframe(자식 document)** 과 맞지 않을 수 있다. 그러면 iframe <head> 의 injectedCss 가
 * 복제 결과에 반영되지 않고, Word 붙여넣기 등으로 박힌 인라인 `word-break: keep-all` 만 남는다.
 * → 표 min-content 가 커져 toSvg 의 고정 뷰박스(너비 DOCUMENT_CAPTURE_DEFAULTS.width) 밖으로 밀리고
 *   PNG 우측이 잘리는 현상(상세 HTML 은 정상, 캡처만 비정상)으로 이어질 수 있다.
 *
 * 해결: toPng 직전 **같은 iframe DOM** 에 표·td/th·셀 자손에 줄바꿈 관련 스타일을
 * `setProperty(..., 'important')` 로 직접 박는다. 클론이 이 인라인을 그대로 가져가므로 안정적이다.
 *
 * 재발 시 점검: 파일 상단 주석 블록 [표 잘림 재발 시 — 무엇을 중점적으로 볼 것인가].
 * 라이브러리 근거: node_modules/html-to-image/es/clone-node.js — cloneCSSStyle
 */
function inlineTableCaptureStylesForHtmlToImage(rootEl) {
  rootEl.querySelectorAll('table').forEach(function (table) {
    table.style.setProperty('table-layout', 'fixed', 'important');
    table.style.setProperty('width', '100%', 'important');
    table.style.setProperty('max-width', '100%', 'important');
    table.style.setProperty('min-width', '0', 'important');
  });

  const cells = rootEl.querySelectorAll('td, th');
  cells.forEach(function (cell) {
    cell.style.setProperty('min-width', '0', 'important');
    cell.style.setProperty('max-width', '100%', 'important');
    cell.style.setProperty('word-break', 'break-word', 'important');
    cell.style.setProperty('overflow-wrap', 'anywhere', 'important');
    cell.style.setProperty('white-space', 'normal', 'important');
  });

  const cellDescendants = rootEl.querySelectorAll('td * , th *');
  cellDescendants.forEach(function (el) {
    if (el && el.style) {
      el.style.setProperty('word-break', 'break-word', 'important');
      el.style.setProperty('overflow-wrap', 'anywhere', 'important');
      el.style.setProperty('white-space', 'normal', 'important');
      el.style.setProperty('max-width', '100%', 'important');
    }
  });
}

// ============================================================================
// [HTML → PNG 공통] 상신(captureHtmlContent) · 미리보기(captureHtmlAsDataUrl) 공용
// 미리보기만 div+메인문서에 toPng 하면 표가 안 그려지는 브라우저가 있어 iframe 경로를 공유함.
//
// [중요] 이 함수의 기본 옵션은 DOCUMENT_CAPTURE_DEFAULTS에서 가져옵니다.
// 호출부에서 옵션을 넘기지 않으면 DOCUMENT_CAPTURE_DEFAULTS 값이 적용됩니다.
// ============================================================================

/**
 * HTML 문자열을 iframe 에서 렌더 후 toPng (레이아웃 폭/높이 포함).
 * 표가 PNG 에서만 잘리면 파일 상단 [표 잘림 재발 시 — 무엇을 중점적으로 볼 것인가] 를 먼저 본다.
 *
 * @param {string} htmlContent - 비어 있지 않은 HTML
 * @param {Object} options
 * @returns {Promise<{dataUrl: string, width: number, height: number}|null>}
 */
async function captureHtmlToPngLayout(htmlContent, options = {}) {
  const {
    width = DOCUMENT_CAPTURE_DEFAULTS.width,
    quality = DOCUMENT_CAPTURE_DEFAULTS.quality,
    pixelRatio = DOCUMENT_CAPTURE_DEFAULTS.pixelRatio,
    backgroundColor = DOCUMENT_CAPTURE_DEFAULTS.backgroundColor,
    padding = DOCUMENT_CAPTURE_DEFAULTS.padding,
  } = options;

  // [중요] forceResponsiveTables: false — 표의 인라인 width를 유지해야 에디터와 동일한 열 폭으로 캡처됨.
  // 이전에 true였을 때: table/td/th/col의 인라인 width가 전부 삭제되어
  // 각 셀이 텍스트 내용만큼만 줄어들고, 결과적으로 캡처 이미지에서 표가 작아 보이는 문제가 발생했음.
  // 넓은 표가 본문 폭(734px)을 초과하는 경우는 injectedCss 의 max-width:100%로 컨테이너 안쪽에 눌러 맞춤.
  const normalizedHtml = normalizeHtmlForCKEditor(htmlContent, {
    stripTableMarginLeft: true,
    stripNegativeTextIndent: true,
    removeFontSize: false,
    forceResponsiveTables: false,
  });

  /* 본 문서 트리 밖 div만 두고 toPng 하면 foreignObject 가 table 을 안 그리는 경우가 있음.
     완전한 HTML 문서(iframe) 안에서 렌더링 후 캡처한다. */
  /* [표 폭 / 잘림 방지 — 구현 갱신 요약]
     1) injectedCss: 래퍼(.table) overflow:hidden, table 은 width/max-width:100% + table-layout:fixed
        + min-width:0(래퍼 안에 눌러 맞춤). td/th 및 자손에 overflow-wrap·word-break(모두 !important).
     2) shrinkOverflowingTables: 인라인 col/td width 로 인해 래퍼를 넘는 표만 col/cell width 제거 후
        width:100% 인라인(레거시 돕기).
     3) inlineTableCaptureStylesForHtmlToImage: toPng 시 html-to-image 가 iframe 노드를 복제할 때
        메인 window 의 getComputedStyle 를 써 <style> 기반 규칙이 클론에 안 실리는 케이스가 있어,
        표·셀·셀 자손에 word-break/overflow-wrap 등을 **인라인 !important** 로 한 번 더 박음(아래 주석). */
  /* [표 잘림 방지 원칙 — html-to-image 제약]
     clone-node.js 는 `window.getComputedStyle(노드)` 에 **호출 측(메인) window** 를 쓴다.
     캡처 root 는 숨김 iframe(자식 document)이므로, iframe <head> 에 주입한 CSS 가
     복제 시 최종 캐스케이드에 반영되지 않고, 붙여넣기 <p style="word-break:keep-all"> 만
     남으면 toSvg 의 SVG viewBox(734×height) 밖으로 표가 밀려 **우측이 잘린 PNG**가 된다.
     → toPng 직전(3)으로 동일 DOM 에 인라인 !important 를 박아 클론에도 전달되게 함.
     + table-layout:fixed + overflow-wrap:anywhere 으로 열·셀 min-content 를 래퍼에 맞춤. */
  const injectedCss = `
    .ck-content p { margin: 0 !important; line-height: 1.5 !important; }
    .ck-content img { max-width: 100% !important; height: auto !important; }
    .ck-content figure.table, .ck-content .table {
      max-width: calc(100% - 40px) !important;
      width: calc(100% - 40px) !important;
      margin: 0 20px !important;
      display: block !important;
      overflow: hidden !important;
      box-sizing: border-box !important;
    }
    .ck-content figure.table table,
    .ck-content .table table,
    .ck-content table {
      display: table !important;
      border-collapse: collapse !important;
      table-layout: fixed !important;
      box-sizing: border-box !important;
      width: 100% !important;
      min-width: 0 !important;
      max-width: 100% !important;
      border-spacing: 0 !important;
      border: 1px double hsl(0, 0%, 70%);
    }
    .ck-content tbody { display: table-row-group !important; }
    .ck-content thead { display: table-header-group !important; }
    .ck-content tr { display: table-row !important; }
    .ck-content figure.table td, .ck-content figure.table th,
    .ck-content .table td, .ck-content .table th,
    .ck-content table td, .ck-content table th {
      display: table-cell !important;
      box-sizing: border-box !important;
      word-break: break-word !important;
      overflow-wrap: anywhere !important;
      white-space: normal !important;
      min-width: 0 !important;
      max-width: 100% !important;
      padding: .4em;
      border: 1px solid hsl(0, 0%, 75%);
    }
    /* 셀 자손 — 붙여넣기 <p> 의 keep-all 등(클론에 <style> 미반영 시에도 toPng 전 인라인으로 재보강) */
    .ck-content figure.table td *, .ck-content figure.table th *,
    .ck-content .table td *, .ck-content .table th *,
    .ck-content table td *, .ck-content table th * {
      word-break: break-word !important;
      overflow-wrap: anywhere !important;
      white-space: normal !important;
      max-width: 100% !important;
    }
  `;

  const iframe = document.createElement('iframe');
  iframe.setAttribute('title', 'document-capture');
  iframe.setAttribute('aria-hidden', 'true');
  /* 높이는 작게 두어도 내부 문서 scrollHeight는 본문 높이를 반영한다. 과대한 iframe 높이는
     documentElement.scrollHeight 오판(하얀 여백 PNG)의 원인이 될 수 있음. */
  iframe.style.cssText =
    'position:fixed;left:-20000px;top:0;width:2400px;height:320px;border:0;margin:0;padding:0;opacity:0.01;pointer-events:none;';

  document.body.appendChild(iframe);

  const PLACEHOLDER_IMG =
    'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==';

  try {
    const idoc = iframe.contentWindow.document;
    idoc.open();
    idoc.write('<!DOCTYPE html><html><head><meta charset="utf-8"></head><body></body></html>');
    idoc.close();
    /* html/body 가 뷰포트만큼 불필요하게 늘어나지 않게: 높이 측정은 오직 .ck-content(root)만 사용 */
    idoc.documentElement.style.overflow = 'visible';
    idoc.documentElement.style.height = 'auto';
    idoc.body.style.margin = '0';
    idoc.body.style.padding = '0';
    idoc.body.style.overflow = 'visible';
    idoc.body.style.height = 'auto';
    idoc.body.style.minHeight = '0';
    idoc.body.style.backgroundColor = backgroundColor;

    const style = idoc.createElement('style');
    style.textContent = injectedCss;
    idoc.head.appendChild(style);

    const root = idoc.createElement('div');
    root.className = 'ck-content';
    root.style.cssText = `
    position: relative;
    left: 0;
    top: 0;
    width: ${width}px;
    background-color: ${backgroundColor};
    padding: ${padding}px;
    box-sizing: border-box;
    font-family: '맑은 고딕', 'Malgun Gothic', 'Noto Serif CJK SC', 'Noto Serif CJK KR', serif, sans-serif;
    font-size: 14px;
    line-height: 1.5;
    color: #333;
    overflow: visible;
  `;
    root.innerHTML = normalizedHtml;
    idoc.body.appendChild(root);

    unwrapFigureTablesForCapture(root);

    let contentHeight;
    // PNG 가로는 항상 baseWidth(734)로 고정. 넓은 표는 CSS max-width:100% 로 컨테이너에 눌러 맞춤.
    // (예전엔 표 자연 폭에 맞춰 PNG 를 키우고 표시 측에서 축소했으나, 본문·헤더 텍스트까지 함께
    //  작아 보이는 문제가 있어 "표만 줄이는" 방식으로 되돌림.)
    const actualWidth = width;

    const images = root.querySelectorAll('img');
    if (images.length > 0) {
      await Promise.all(
        Array.from(images).map(function (img) {
          return new Promise(function (resolve) {
            function done() {
              resolve();
            }
            img.onerror = function (e) {
              if (e && e.stopPropagation) e.stopPropagation();
              if (e && e.preventDefault) e.preventDefault();
              done();
            };
            if (img.complete && img.naturalWidth > 0 && img.naturalHeight > 0) {
              done();
            } else {
              img.onload = done;
            }
          });
        }),
      );
      const broken = [];
      images.forEach(function (img) {
        if (!img.complete || img.naturalWidth === 0 || img.naturalHeight === 0) {
          img.removeAttribute('srcset');
          img.src = PLACEHOLDER_IMG;
          broken.push(img);
        }
      });
      if (broken.length > 0) {
        await Promise.all(
          broken.map(function (img) {
            return new Promise(function (resolve) {
              if (img.complete && img.naturalWidth > 0) {
                resolve();
                return;
              }
              img.onload = resolve;
              img.onerror = resolve;
              setTimeout(resolve, 200);
            });
          }),
        );
      }
    }

    await new Promise(function (resolve) {
      requestAnimationFrame(function () {
        requestAnimationFrame(resolve);
      });
    });
    await new Promise(function (resolve) {
      setTimeout(resolve, 150);
    });

    /* 레이아웃이 끝난 뒤, 래퍼 폭을 넘어서 그려진 표만 골라 축소하고 재레이아웃을 기다린 뒤
       contentHeight 를 측정한다. 표가 줄어들면서 셀 텍스트가 여러 줄로 늘어나 세로 길이가
       바뀌므로, 축소 후에 높이를 재야 PNG 에 잘림 없이 담긴다. */
    shrinkOverflowingTables(root);
    /* html-to-image: iframe 스타일시트가 클론에 안 실리는 케이스 대비(파일 상단 [표 잘림 재발 시] 참고). */
    inlineTableCaptureStylesForHtmlToImage(root);
    await new Promise(function (resolve) {
      requestAnimationFrame(function () {
        requestAnimationFrame(resolve);
      });
    });

    contentHeight = measureCaptureContentHeight(root);

    /* 레이아웃이 안정된 다음 toPng: foreignObject 스냅샷 타이밍 이슈 완화 */
    await new Promise(function (resolve) {
      requestAnimationFrame(function () {
        requestAnimationFrame(resolve);
      });
    });

    /* width/height 는 캔버스/클립 크기. 본문이 길수록 contentHeight 가 커지므로 세로는 자동으로 늘어남. */
    const dataUrl = await toPng(root, {
      quality,
      pixelRatio,
      backgroundColor,
      width: actualWidth,
      height: contentHeight,
      skipFonts: true,
      includeQueryParams: true,
      cacheBust: true,
      style: {
        overflow: 'visible',
      },
    });

    return { dataUrl, width: actualWidth, height: contentHeight };
  } catch (error) {
    console.error('HTML 콘텐츠 이미지 캡쳐 실패:', error);
    throw error;
  } finally {
    if (iframe.parentNode) {
      iframe.parentNode.removeChild(iframe);
    }
  }
}

// ============================================================================
// [상신용 캡쳐] - 모바일에서 볼 문서 이미지
// 사용처: useDocumentSave.js → handleSubmit()
// 옵션: DOCUMENT_CAPTURE_DEFAULTS 사용 (호출부에서 오버라이드 가능)
// ============================================================================

/**
 * [상신용] HTML 콘텐츠를 임시 DOM에 렌더링하여 PNG 이미지로 캡쳐
 * - 문서 상신 시 호출되어 모바일에서 볼 이미지 생성
 * - CKEditor의 복잡한 CSS 스타일에 영향받지 않음
 * - 전체 본문을 캡쳐하여 모바일에서 스크롤하며 볼 수 있음 (본문이 길면 PNG 세로도 비례해 길어짐)
 *
 * @param {string} htmlContent - 캡쳐할 HTML 문자열 (에디터 콘텐츠)
 * @param {Object} options - 캡쳐 옵션 (DOCUMENT_CAPTURE_DEFAULTS 기반, 부분 오버라이드 가능)
 * @returns {Promise<Uint8Array|null>} PNG 이미지 바이트 배열 (API 전송용)
 */
async function captureHtmlContent(htmlContent, options = {}) {
  if (!htmlContent) {
    console.error('캡쳐할 HTML 콘텐츠가 없습니다.');
    return null;
  }

  try {
    const layout = await captureHtmlToPngLayout(htmlContent, options);
    if (!layout) {
      return null;
    }
    const response = await fetch(layout.dataUrl);
    const blob = await response.blob();
    const arrayBuffer = await blob.arrayBuffer();
    return new Uint8Array(arrayBuffer);
  } catch (error) {
    console.error('HTML 콘텐츠 이미지 캡쳐 실패:', error);
    throw error;
  }
}

/**
 * [상신용] HTML 콘텐츠를 캡쳐하여 Base64 문자열로 반환
 * - 사용처: useDocumentSave.js → handleSubmit()
 * - API 전송에 적합한 형태로 변환
 *
 * @param {string} htmlContent - 캡쳐할 HTML 문자열
 * @param {Object} options - 캡쳐 옵션 (DOCUMENT_CAPTURE_DEFAULTS 기반, 부분 오버라이드 가능)
 * @returns {Promise<string|null>} Base64 인코딩된 PNG 이미지 문자열
 */
async function captureHtmlContentAsBase64(htmlContent, options = {}) {
  const uint8Array = await captureHtmlContent(htmlContent, options);
  if (!uint8Array) return null;

  return uint8ArrayToBase64(uint8Array);
}

/**
 * 이미지 바이트 배열을 Base64 문자열로 변환
 * - API 전송 시 JSON 직렬화를 위해 사용
 *
 * @param {Uint8Array} uint8Array - 이미지 바이트 배열
 * @returns {string} Base64 인코딩된 문자열
 */
function uint8ArrayToBase64(uint8Array) {
  if (!uint8Array) return null;

  let binary = '';
  const len = uint8Array.byteLength;
  for (let i = 0; i < len; i += 1) {
    binary += String.fromCharCode(uint8Array[i]);
  }
  return window.btoa(binary);
}

// ============================================================================
// [레거시 함수] - 사용하지 않음 (하위 호환성 유지)
// ============================================================================

/**
 * @deprecated 사용하지 마세요. captureHtmlContentAsBase64 사용 권장
 * DOM 요소를 PNG 이미지로 캡쳐하는 유틸리티 함수 (레거시)
 */
async function captureDocumentImage(element, options = {}) {
  if (!element) {
    console.error('캡쳐할 요소가 없습니다.');
    return null;
  }

  const { quality = 1, pixelRatio = 3, backgroundColor = '#ffffff' } = options;

  // CKEditor 콘텐츠 영역(.ck-content)을 찾아서 캡처 (툴바 제외)
  const ckContentElement = element.querySelector('.ck-content');
  // CKEditor 메인 컨테이너 (.ck-editor__main) 찾기
  const ckEditorMainElement = element.querySelector('.ck-editor__main');
  // CKEditor 편집 가능 영역 (.ck-editor__editable) 찾기
  const ckEditableElement = element.querySelector('.ck-editor__editable');
  const targetElement = ckContentElement || element;

  // 캡처 모드 클래스 추가할 요소들 목록
  const elementsToAddCaptureMode = [element];
  if (ckContentElement) {
    elementsToAddCaptureMode.push(ckContentElement);
  }
  if (ckEditorMainElement) {
    elementsToAddCaptureMode.push(ckEditorMainElement);
  }
  if (ckEditableElement) {
    elementsToAddCaptureMode.push(ckEditableElement);
  }

  try {
    // 캡처 전 'capture-mode' 클래스 추가 - CSS에서 height/overflow 오버라이드
    elementsToAddCaptureMode.forEach(function (el) {
      el.classList.add('capture-mode');
    });

    // DOM 요소를 PNG data URL로 변환
    const dataUrl = await toPng(targetElement, {
      quality,
      pixelRatio,
      backgroundColor,
      skipFonts: false,
      includeQueryParams: true,
      cacheBust: true,
      filter: function (node) {
        if (node.classList && node.classList.contains('page-break-line')) {
          return false;
        }
        if (node.classList && node.classList.contains('ck-toolbar')) {
          return false;
        }
        if (node.classList && node.classList.contains('ck-toolbar__items')) {
          return false;
        }
        if (node.classList && node.classList.contains('ck-sticky-panel')) {
          return false;
        }
        return true;
      },
    });

    const response = await fetch(dataUrl);
    const blob = await response.blob();
    const arrayBuffer = await blob.arrayBuffer();
    const uint8Array = new Uint8Array(arrayBuffer);

    return uint8Array;
  } catch (error) {
    console.error('문서 이미지 캡쳐 실패:', error);
    return null;
  } finally {
    elementsToAddCaptureMode.forEach(function (el) {
      el.classList.remove('capture-mode');
    });
  }
}

/**
 * [상신용] DOM 요소를 캡쳐하여 Base64 문자열로 반환
 * - 사용처: useDocumentSave.js → handleSubmit() (editorRef가 있을 때)
 * - 실제 화면에 보이는 에디터 DOM을 그대로 캡쳐
 */
async function captureDocumentImageAsBase64(element, options = {}) {
  const uint8Array = await captureDocumentImage(element, options);
  if (!uint8Array) return null;

  return uint8ArrayToBase64(uint8Array);
}

// ============================================================================
// [미리보기용 캡쳐] - PC에서 인쇄 미리보기
// 사용처: DocumentWritePage.jsx → handlePreview()
//         useDocumentActions.js → handlePreview()
//
// [중요] 상신과 동일한 DOCUMENT_CAPTURE_DEFAULTS 옵션을 사용합니다.
// 이전에는 width:754/padding:20으로 상신(734/15)과 달랐으나,
// 같은 문서인데 미리보기와 상신 결과가 달라지는 문제를 해결하기 위해 통일했습니다.
// 미리보기만 다르게 하고 싶은 옵션(예: pixelRatio)은 호출부에서 오버라이드하세요.
// ============================================================================

/**
 * [미리보기용] HTML 콘텐츠를 PNG Data URL로 캡쳐하고 실제 높이를 반환
 * - 사용처: DocumentWritePage.jsx, useDocumentActions.js
 * - 렌더·정규화·unwrap 은 captureHtmlToPngLayout(상신과 동일)
 * - 상신과의 차이: 반환 형태만 다름 (dataUrl + width + height). 슬라이스(페이지 분할)용.
 *
 * @param {string} htmlContent - 캡쳐할 HTML
 * @param {Object} [options] - DOCUMENT_CAPTURE_DEFAULTS 기반, 부분 오버라이드 가능
 * @returns {Promise<{dataUrl: string, width: number, height: number}|null>}
 */
async function captureHtmlAsDataUrl(htmlContent, options = {}) {
  if (!htmlContent) return null;

  try {
    const layout = await captureHtmlToPngLayout(htmlContent, options);
    if (!layout) {
      return null;
    }
    return { dataUrl: layout.dataUrl, width: layout.width, height: layout.height };
  } catch (error) {
    console.error('Capture failed:', error);
    return null;
  }
}

export {
  DOCUMENT_CAPTURE_DEFAULTS,
  captureHtmlAsDataUrl,
  captureHtmlContent,
  captureHtmlContentAsBase64,
  captureDocumentImage,
  captureDocumentImageAsBase64,
  uint8ArrayToBase64,
};
export default captureHtmlContent;
